import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Building, Users, Clock, CheckCircle2, XCircle, ChevronDown, ChevronRight, Search } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';

export default function SuperAdminPage() {
  const [search, setSearch] = useState('');
  const [expandedTenant, setExpandedTenant] = useState<string | null>(null);
  const qc = useQueryClient();

  const { data: overview } = useQuery({
    queryKey: ['admin-overview'],
    queryFn: () => api.admin.overview(),
  });

  const { data: tenantsData } = useQuery({
    queryKey: ['admin-tenants'],
    queryFn: () => api.admin.tenants(),
  });

  const approveMutation = useMutation({
    mutationFn: (tenantId: string) => api.admin.approveTenant(tenantId),
    onSuccess: () => { toast.success('Tenant approved'); qc.invalidateQueries({ queryKey: ['admin-tenants'] }); },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ tenantId, reason }: { tenantId: string; reason: string }) => api.admin.rejectTenant(tenantId, reason),
    onSuccess: () => { toast.success('Tenant rejected'); qc.invalidateQueries({ queryKey: ['admin-tenants'] }); },
  });

  const tenants = (tenantsData?.data ?? []) as Record<string, unknown>[];
  const filtered = search ? tenants.filter((t) => ((t.name as string) ?? '').toLowerCase().includes(search.toLowerCase())) : tenants;
  const stats = overview?.data as Record<string, number> | undefined;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-white">Super Admin</h1>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Tenants', value: stats?.totalTenants ?? 0, icon: Building },
          { label: 'Active Trials', value: stats?.activeTrials ?? 0, icon: Clock },
          { label: 'Pending Review', value: stats?.pendingReview ?? 0, icon: Users },
          { label: 'Recent Signups (7d)', value: stats?.recentSignups ?? 0, icon: CheckCircle2 },
        ].map((s) => (
          <div key={s.label} className="bg-gray-800/50 rounded-lg p-4">
            <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
              <s.icon className="w-4 h-4" /> {s.label}
            </div>
            <div className="text-2xl font-bold text-white">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tenants..."
          className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm" />
      </div>

      {/* Tenant list */}
      <div className="space-y-2">
        {filtered.map((tenant) => {
          const id = tenant.tenantId as string;
          const expanded = expandedTenant === id;
          const trialDays = tenant.trialEndsAt ? Math.ceil((new Date(tenant.trialEndsAt as string).getTime() - Date.now()) / 86400000) : null;

          return (
            <div key={id} className="bg-gray-800/50 rounded-lg overflow-hidden">
              <button onClick={() => setExpandedTenant(expanded ? null : id)}
                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-800/80"
                aria-expanded={expanded}>
                <div className="flex items-center gap-3">
                  {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                  <div>
                    <div className="text-white font-medium text-sm">{tenant.name as string}</div>
                    <div className="text-xs text-gray-400">
                      {tenant.userCount as number} users &middot; {(tenant.planType as string) ?? 'trial'} &middot;
                      {trialDays != null && trialDays > 0 ? ` ${trialDays}d trial left` : ''}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    tenant.onboardingStatus === 'active' ? 'bg-green-500/20 text-green-400' :
                    tenant.onboardingStatus === 'noeted_review' ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-gray-600/20 text-gray-400'
                  }`}>{(tenant.onboardingStatus as string)?.replace(/_/g, ' ') ?? 'pending'}</span>
                </div>
              </button>

              {expanded && (
                <div className="border-t border-gray-700 px-4 py-4 space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    <div><span className="text-gray-400">Facility:</span> <span className="text-white">{(tenant.facilityType as string)?.replace(/_/g, ' ') ?? '—'}</span></div>
                    <div><span className="text-gray-400">Size:</span> <span className="text-white">{(tenant.orgSize as string) ?? '—'}</span></div>
                    <div><span className="text-gray-400">Frameworks:</span> <span className="text-white">{((tenant.frameworks as string[]) ?? []).join(', ') || '—'}</span></div>
                    <div><span className="text-gray-400">States:</span> <span className="text-white">{((tenant.states as string[]) ?? []).join(', ') || '—'}</span></div>
                    <div><span className="text-gray-400">Created:</span> <span className="text-white">{new Date(tenant.createdAt as string).toLocaleDateString()}</span></div>
                  </div>

                  <div className="flex gap-2">
                    <button onClick={() => approveMutation.mutate(id)} disabled={approveMutation.isPending}
                      className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white rounded text-sm">
                      <CheckCircle2 className="w-4 h-4" /> Approve
                    </button>
                    <button onClick={() => rejectMutation.mutate({ tenantId: id, reason: 'Needs additional information' })}
                      className="flex items-center gap-1 px-3 py-1.5 bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded text-sm border border-red-800">
                      <XCircle className="w-4 h-4" /> Reject
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
